import React, { useState } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';

const PRESET_COLORS = [
  '#BF2634',
  '#39FF14',
  '#E5C04B',
  '#5391FF',
  '#FF6B35',
  '#C724B1',
  '#00C9A7',
  '#565656',
];

function CloseButton({ onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: 'none',
        border: 'none',
        color: hovered ? '#F5F5F5' : '#565656',
        fontSize: '20px',
        cursor: 'pointer',
        lineHeight: 1,
        padding: '0 2px',
        transition: 'color 0.15s ease',
      }}
    >
      ✕
    </button>
  );
}

export default function CreateWorkspaceModal({ isOpen, onClose, onSuccess }) {
  const { createWorkspace } = useWorkspace();
  const [name, setName] = useState('');
  const [selectedColor, setSelectedColor] = useState('#BF2634');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nameFocused, setNameFocused] = useState(false);
  const [cancelHover, setCancelHover] = useState(false);
  const [createHover, setCreateHover] = useState(false);

  if (!isOpen) return null;

  function resetForm() {
    setName('');
    setSelectedColor('#BF2634');
    setError(null);
    setIsLoading(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) {
      setError('Workspace name is required.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await createWorkspace(name.trim(), selectedColor);
      onSuccess();
      onClose();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create workspace. Please try again.');
      setIsLoading(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#2a2a2a',
          border: '1px solid #3a3a3a',
          borderRadius: '10px',
          width: '480px',
          maxWidth: '90vw',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6), 0 0 60px rgba(57, 255, 20, 0.08)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: '#222222',
            padding: '20px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #3a3a3a',
          }}
        >
          <span
            style={{
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 700,
              fontSize: '20px',
              color: '#FFFFFF',
            }}
          >
            Create Workspace
          </span>
          <CloseButton onClick={onClose} />
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '32px 24px' }}>
            {/* Name field */}
            <label
              style={{
                display: 'block',
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 500,
                fontSize: '14px',
                color: '#F5F5F5',
                marginBottom: '6px',
              }}
            >
              Workspace Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onFocus={() => setNameFocused(true)}
              onBlur={() => setNameFocused(false)}
              placeholder="e.g. Stacked Hearts Productions"
              style={{
                width: '100%',
                backgroundColor: '#222222',
                border: `1px solid ${nameFocused ? '#39FF14' : '#3a3a3a'}`,
                borderRadius: '4px',
                padding: '12px 16px',
                color: '#F5F5F5',
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 400,
                fontSize: '16px',
                outline: 'none',
                marginBottom: '24px',
                boxSizing: 'border-box',
              }}
            />

            {/* Color field */}
            <label
              style={{
                display: 'block',
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 500,
                fontSize: '14px',
                color: '#F5F5F5',
                marginBottom: '6px',
              }}
            >
              Workspace Color
            </label>
            <div
              style={{
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 300,
                fontSize: '13px',
                color: '#888888',
                marginBottom: '12px',
              }}
            >
              Used for your workspace identifier
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {PRESET_COLORS.map((color) => (
                <div
                  key={color}
                  onClick={() => setSelectedColor(color)}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: color,
                    cursor: 'pointer',
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                    transform: selectedColor === color ? 'scale(1.15)' : 'scale(1)',
                    boxShadow:
                      selectedColor === color
                        ? `0 0 0 3px #FFFFFF, 0 0 0 5px ${color}`
                        : 'none',
                  }}
                />
              ))}
            </div>

            {/* Error */}
            {error && (
              <div
                style={{
                  color: '#BF2634',
                  fontFamily: "'Roboto', sans-serif",
                  fontWeight: 500,
                  fontStyle: 'italic',
                  fontSize: '14px',
                  backgroundColor: 'rgba(191, 38, 52, 0.1)',
                  borderLeft: '3px solid #BF2634',
                  padding: '10px 14px',
                  borderRadius: '4px',
                  marginTop: '16px',
                }}
              >
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid #3a3a3a',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
            }}
          >
            <button
              type="button"
              onMouseEnter={() => setCancelHover(true)}
              onMouseLeave={() => setCancelHover(false)}
              onClick={onClose}
              style={{
                background: 'transparent',
                border: `1px solid ${cancelHover ? '#F5F5F5' : '#565656'}`,
                color: '#F5F5F5',
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 500,
                fontSize: '14px',
                padding: '10px 20px',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'border-color 0.15s ease',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              onMouseEnter={() => setCreateHover(true)}
              onMouseLeave={() => setCreateHover(false)}
              style={{
                backgroundColor: createHover && !isLoading ? '#8B1C26' : '#BF2634',
                color: '#FFFFFF',
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 500,
                fontSize: '14px',
                padding: '10px 20px',
                borderRadius: '4px',
                border: 'none',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.15s ease',
              }}
            >
              {isLoading ? 'Creating...' : 'Create Workspace'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
